import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { API_URL } from '../config/api'
import './ProjectDetail.css'

function ProjectDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [relatedProjects, setRelatedProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)

  const getDescriptionText = (desc) => {
    if (typeof desc === 'string') return desc
    if (Array.isArray(desc)) {
      return desc
        .map(block => block.children?.map(child => child.text).join('') || '')
        .join(' ')
    }
    return ''
  }

  const getImageUrl = (image) => {
    if (!image) return null
    if (Array.isArray(image.data)) {
      return image.data[0]?.attributes?.url
    }
    return image.data?.attributes?.url || image.url
  }

  const getGalleryImages = (imageField) => {
    if (!imageField) return []
    if (Array.isArray(imageField.data)) {
      return imageField.data.map(img => img.attributes?.url || img.url).filter(Boolean)
    }
    return []
  }

  const goToPreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
    )
    setAutoPlay(false)
  }

  const goToNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
    )
    setAutoPlay(false)
  }

  const goToImage = (index) => {
    setCurrentImageIndex(index)
    setAutoPlay(false)
  }

  // Get gallery images array from the image field (which can have multiple images)
  const galleryImages = project ? getGalleryImages(project.image) : []

  // Auto-play slideshow
  useEffect(() => {
    if (!autoPlay || galleryImages.length === 0) return

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
      )
    }, 5000) // Change image every 5 seconds

    return () => clearInterval(interval)
  }, [autoPlay, galleryImages.length])

  useEffect(() => {
    const fetchProjectDetail = async () => {
      try {
        setLoading(true)
        const response = await fetch(`${API_URL}/api/projects/${id}?populate=*`)

        if (!response.ok) {
          throw new Error('Project not found')
        }

        const data = await response.json()
        const projectData = {
          id: data.data.id,
          ...data.data.attributes
        }
        setProject(projectData)

        // Fetch related projects (all projects, then filter)
        const allProjectsResponse = await fetch(`${API_URL}/api/projects?populate=*`)
        const allProjectsData = await allProjectsResponse.json()
        const relatedProjects = (allProjectsData.data || [])
          .map(item => ({
            id: item.id,
            ...item.attributes
          }))
          .filter(p => p.id !== parseInt(id))
          .slice(0, 3) // Show 3 related projects

        setRelatedProjects(relatedProjects)
      } catch (err) {
        console.error('Error fetching project:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchProjectDetail()
  }, [id])

  if (loading) {
    return (
      <div className="project-detail">
        <div className="loading">Projectgegevens worden geladen...</div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="project-detail">
        <div className="error-container">
          <h2>Project Niet Gevonden</h2>
          <p>{error || 'Het project dat je zoekt bestaat niet.'}</p>
          <Link to="/projects" className="btn btn-primary">Terug naar Projecten</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="project-detail">
      {/* Back Button */}
      <button className="back-button" onClick={() => navigate('/projects')}>
        ← Terug naar Projecten
      </button>

      {/* Hero Section with Main Image & Overlay */}
      {project.image && getImageUrl(project.image) && (
        <div className="project-hero">
          <img
            src={`${API_URL}${getImageUrl(project.image)}`}
            alt={project.title}
            className="hero-image"
          />

          {/* Overlay Content */}
          <div className="hero-overlay"></div>

          {/* Top Left - Title */}
          <div className="hero-top-left">
            <h1>{project.title}</h1>
          </div>

          {/* Bottom Right - Date & Details */}
          <div className="hero-bottom-right">
            {project.date && (
              <p className="project-meta-date">
                {new Date(project.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
            {project.description && (
              <div className="project-description">
                <p>{getDescriptionText(project.description)}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gallery Slideshow Section */}
      {galleryImages.length > 1 && (
        <div className="project-gallery">
          <h2>Projectgalerie</h2>
          <div className="slideshow-container">
            {/* Main Slideshow */}
            <div className="slideshow">
              {galleryImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className={`slide ${index === currentImageIndex ? 'active' : ''}`}
                >
                  <img
                    src={`${API_URL}${imageUrl}`}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="slide-image"
                  />
                </div>
              ))}
            </div>

            {/* Controls below the image */}
            <div className="slide-controls">
              <button className="slide-button prev" onClick={goToPreviousImage}>
                ← Vorige
              </button>

              <div className="slide-controls-center">
                <div className="slide-counter">
                  {currentImageIndex + 1} / {galleryImages.length}
                </div>
                <button
                  className={`autoplay-toggle ${autoPlay ? 'active' : ''}`}
                  onClick={() => setAutoPlay(!autoPlay)}
                  title={autoPlay ? 'Slideshow pauzeren' : 'Slideshow afspelen'}
                >
                  {autoPlay ? '⏸' : '▶'}
                </button>
              </div>

              <button className="slide-button next" onClick={goToNextImage}>
                Volgende →
              </button>
            </div>

            {/* Thumbnail Navigation */}
            {galleryImages.length > 1 && (
              <div className="gallery-thumbnails">
                {galleryImages.map((imageUrl, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                  >
                    <img
                      src={`${API_URL}${imageUrl}`}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <div className="related-projects">
          <h2>Gerelateerde Projecten</h2>
          <div className="related-grid">
            {relatedProjects.map((relProject) => (
              <Link
                key={relProject.id}
                to={`/projects/${relProject.id}`}
                className="related-card"
              >
                {relProject.image && getImageUrl(relProject.image) && (
                  <img
                    src={`${API_URL}${getImageUrl(relProject.image)}`}
                    alt={relProject.title}
                    className="related-image"
                  />
                )}
                <div className="related-info">
                  <h3>{relProject.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="project-navigation">
        <Link to="/projects" className="btn btn-secondary">Bekijk Alle Projecten</Link>
      </div>
    </div>
  )
}

export default ProjectDetail
